import React from 'react'
import { ContextNotification, Modal } from '@pandiafi/components'
import { Button, Dropdown } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'
import { fmtCurrency } from './dayReservableAreas.helpers'
import { todayISO } from '../../utils/dateUtils'
import { areaAddressLabel, areaPriceFor, areaTitle } from './uiSelectors'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'
import { useAPIGetContractSuspense } from '../../api/apiHooks/useAPIGetContract'
import { ResidentDetailsHtml } from '../../components/ResidentDetailsHtml/ResidentDetailsHtml'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useAPICreateContractDayReservableAreaReservation } from 'api/apiHooks/useAPICreateContractDayReservableAreaReservation'
import { ReservationTermsAcceptance } from 'components/ReservationTermsAcceptance/ReservationTermsAcceptance'
import { ReservationRulesAndTermsLink } from 'components/ReservationRulesAndTermsLinks/ReservationRulesAndTermsLinks'

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

const getErrorStatus = (err: unknown): number | undefined => {
  if (!isRecord(err)) return undefined
  const ed = isRecord(err.errorDetails) ? err.errorDetails : undefined
  if (ed && typeof ed.statusCode === 'number') return ed.statusCode
  if (typeof (err as { statusCode?: unknown }).statusCode === 'number')
    return (err as { statusCode: number }).statusCode
  const resp = isRecord((err as { response?: unknown }).response)
    ? (err as { response: { status?: unknown } }).response
    : undefined
  if (resp && typeof resp.status === 'number') return resp.status
  return undefined
}

const getErrorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message
  if (isRecord(err) && typeof err.message === 'string') return err.message
  const resp =
    isRecord(err) &&
    isRecord(err.errorDetails) &&
    isRecord(err.errorDetails.response)
      ? (err.errorDetails.response as Record<string, unknown>)
      : undefined
  if (resp && typeof resp.message === 'string') return resp.message as string
  return undefined
}

export const DayReservableAreaReservationForm: React.FC<{
  area: CoreDayReservableArea
  onSuccess: (effectiveFromISO: string) => void
  onClose?: () => void
  residentDetails: string | null
}> = ({ area, onSuccess, onClose, residentDetails }) => {
  const { t, i18n } = useTranslation(['storage'])
  const addressLabel = areaAddressLabel(area)
  const { currentContractId } = useCurrentContract()
  const { data: contract } = useAPIGetContractSuspense(currentContractId)
  const moduleConfig = useModuleConfig()

  const contractStart = DateTime.fromISO(contract.startDate).startOf('day')

  const price = areaPriceFor(area, null)
  const priceLabel =
    typeof price === 'number' ? fmtCurrency(price, i18n.language) : undefined

  const { mutate: createReservation, isPending } =
    useAPICreateContractDayReservableAreaReservation(currentContractId)

  const today = todayISO()
  const nextMonthFirstDate = DateTime.now().plus({ months: 1 }).startOf('month')
  const nextMonthFirstISO = nextMonthFirstDate.toISODate()
  const nextMonthFirst = nextMonthFirstDate.toFormat('dd.MM.yyyy')

  const isFirstDayOfMonth = DateTime.now().day === 1

  const startOptions = [
    { id: 'today', label: t('startOption.today') },
    {
      id: 'next_month',
      label: nextMonthFirst,
    },
  ]

  const [startOption, setStartOption] = React.useState<'today' | 'next_month'>(
    'today'
  )
  const [acceptTerms, setAcceptTerms] = React.useState(false)
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

  React.useEffect(() => {
    setAcceptTerms(false)
    setErrorMsg(null)
    setStartOption('today')
  }, [area?.rentableHierarchyId])

  const canSubmit =
    !!currentContractId &&
    !!area?.rentableHierarchyId &&
    acceptTerms &&
    !isPending

  const onSubmit = () => {
    if (!currentContractId) {
      setErrorMsg(t('error.contractMissing'))
      return
    }

    const startISO =
      startOption === 'today' ? today : (nextMonthFirstISO ?? null)
    if (!startISO) {
      setErrorMsg(t('error.reservationFailed'))
      return
    }
    const startDate = startISO
      ? DateTime.fromISO(startISO).startOf('day')
      : null

    if (startDate && startDate < contractStart) {
      setErrorMsg(t('error.reservationStartBeforeContractStart'))
      return
    }
    if (!area?.rentableHierarchyId) return

    setErrorMsg(null)
    createReservation(
      {
        starts: startOption,
        rentableHierarchyId: area.rentableHierarchyId,
      },
      {
        onSuccess: () => onSuccess(startISO ?? ''),
        onError: (e: unknown) => {
          const fallback = t('error.reservationFailed')
          const statusCode = getErrorStatus(e)
          if (statusCode === 409) {
            setErrorMsg(t('error.overlap'))
            return
          }
          setErrorMsg(getErrorMessage(e) || fallback)
        },
      }
    )
  }

  const moduleLinks = moduleConfig?.storages
  const storageFallbackLinks: ReservationRulesAndTermsLink[] = []
  if (moduleLinks?.contractDocumentLink) {
    storageFallbackLinks.push({
      href: moduleLinks.contractDocumentLink,
      label: t('storage:modal.rulesLink'),
    })
  }
  if (moduleLinks?.termsOfUseDocumentLink) {
    storageFallbackLinks.push({
      href: moduleLinks.termsOfUseDocumentLink,
      label: t('storage:modal.termsLink'),
    })
  }

  return (
    <>
      <Modal.Content>
        {(!currentContractId || errorMsg) && (
          <ContextNotification type="error" className="mb-4">
            {errorMsg || t('error.contractMissing')}
          </ContextNotification>
        )}

        <div className="font-bold text-lg">{areaTitle(area)}</div>
        <div className="font-semibold mb-6">
          {`${t('price.label')}: ${t('price.perMonth', { amount: priceLabel })}`}
        </div>

        <ResidentDetailsHtml html={residentDetails} className="my-4" />

        <div className="mb-6">
          <label htmlFor="startOption" className="font-medium">
            {t`effectiveFrom`}*
          </label>
          <Dropdown
            id="startOption"
            value={startOptions.find((opt) => opt.id === startOption)}
            onChange={(option) => {
              if (option) {
                setStartOption(option.id as 'today' | 'next_month')
              }
            }}
            options={startOptions}
            disabled={isFirstDayOfMonth}
          />
        </div>

        <ReservationTermsAcceptance
          contractId={currentContractId}
          reservableCategory="storage"
          realPropertyHierarchyId={area.realPropertyHierarchyId}
          fallbackLinks={storageFallbackLinks}
          accepted={acceptTerms}
          onAcceptedChange={setAcceptTerms}
        >
          {t('view.acceptTerms', { address: addressLabel })}
        </ReservationTermsAcceptance>
      </Modal.Content>
      <Modal.Buttons>
        <Button variant="cancel" onClick={onClose}>
          {t('view.cancel')}
        </Button>
        <Button onClick={onSubmit} disabled={!canSubmit} loading={isPending}>
          {t('view.reserve')}
        </Button>
      </Modal.Buttons>
    </>
  )
}
